import React, { forwardRef } from "react";
import clsx from "clsx";

type Theme = "light" | "dark";

export type ButtonState = "default" | "hovered" | "selected";
export type ButtonColor = "default" | "primary" | "secondary";
export type ButtonVariant =
  | "fill"
  | "text"
  | "text-icon"
  | "text-bold"
  | "link";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  hidden?: boolean;
  type?: "button" | "submit" | "reset";
  active?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  primary?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  isDropDown?: boolean;
  thin?: boolean;
  width?: number;
  long?: boolean;
  height?: number;
  borderLess?: boolean;
  customClassName?: string;
  loading?: boolean;
  themeProp?: Theme;
  /** Design-system variant. When set, overrides legacy fill styles. */
  variant?: ButtonVariant;
  /** Design-system color (used with `variant`). */
  color?: ButtonColor;
  /** Forced state for design-system variants (Default | Hovered | Selected). */
  state?: ButtonState;
  /** Optional trailing/leading icon for `variant="text-icon"`. */
  icon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      hidden,
      type = "button",
      active,
      fullWidth,
      children,
      onClick,
      primary,
      secondary,
      disabled,
      isDropDown,
      thin,
      width,
      long,
      height,
      borderLess = false,
      customClassName,
      loading,
      themeProp,
      variant,
      color,
      state,
      icon,
      ...props
    },
    ref
  ) => {
    const theme: Theme = themeProp || "dark";

    // Every primary / fill Button renders as the v1 "button" pill (dark pill +
    // red outline + offset gray ring + pink cap, fills red on hover; styles in
    // globals.css `.ecq-cta`). Covers both the legacy `primary` prop and the
    // design-system `variant="fill" color="primary"` API. Secondary / text /
    // link / borderless buttons keep their existing styles (v1 renders those
    // plainly too).
    const wantsFill = variant === "fill" || (!variant && !!primary);
    const isPrimaryFill =
      wantsFill && (color ?? (primary ? "primary" : "default")) === "primary";
    if (isPrimaryFill) {
      return (
        <button
          ref={ref}
          {...props}
          type={type}
          onClick={onClick}
          disabled={disabled}
          // The size props travel as custom properties, not as inline
          // width/height: globals.css shrinks the pill on phones, and an inline
          // declaration would win over that media query. `.ecq-cta` reads both
          // vars and falls back to `auto` when a call site passes neither.
          style={
            {
              "--ecq-cta-w": fullWidth
                ? "100%"
                : width
                  ? `${width}px`
                  : undefined,
              // Read only by the phone media query, which otherwise makes every
              // pill size to its label.
              "--ecq-cta-w-sm": fullWidth ? "100%" : undefined,
              "--ecq-cta-h": height ? `${height}px` : undefined,
            } as React.CSSProperties
          }
          className={clsx("ecq-cta", hidden && "invisible", customClassName)}
        >
          <span className="ecq-cta__text">
            {loading ? "…" : children}
          </span>
          <span className="ecq-cta__gray" aria-hidden />
          <span className="ecq-cta__pink" aria-hidden />
          <span className="ecq-cta__tick" aria-hidden />
        </button>
      );
    }

    if (variant) {
      const resolvedColor: ButtonColor =
        color ?? (primary ? "primary" : "default");
      const resolvedState: ButtonState =
        state ?? (active ? "selected" : "default");
      return (
        <DesignSystemButton
          ref={ref}
          variant={variant}
          color={resolvedColor}
          state={resolvedState}
          disabled={disabled}
          icon={icon}
          isDropDown={isDropDown}
          onClick={onClick}
          type={type}
          customClassName={customClassName}
          fullWidth={fullWidth}
          width={width}
          height={height}
          {...props}
        >
          {children}
        </DesignSystemButton>
      );
    }

    // Calculate padding based on height
    const getPaddingClasses = (): string => {
      if (height) {
        // When custom height is provided, use minimal padding and let flexbox center the content
        return "px-4 py-0";
      }

      // Default size-based padding
      if (thin) {
        return "px-2 py-1";
      } else if (long) {
        return "px-6 py-2";
      } else {
        return "px-4 py-2";
      }
    };

    // Calculate height classes (only when no custom height is provided)
    const getHeightClasses = (): string => {
      if (height) {
        return ""; // No height class when custom height is provided
      }

      if (thin) {
        return "h-8";
      } else if (long) {
        return "h-10";
      } else {
        return "h-9";
      }
    };

    return (
      <button
        ref={ref}
        {...props}
        onClick={onClick}
        type={type}
        disabled={disabled}
        style={{
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
          minHeight: height ? `${height}px` : undefined, // Ensure minimum height
        }}
        className={clsx(
          // Base styles
          "flex justify-center items-center gap-2",
          "rounded-[8px] font-semibold transition-all duration-200",
          "border border-solid cursor-pointer",

          // Size variants - conditional padding and height
          getPaddingClasses(),
          getHeightClasses(),
          fullWidth && "w-full",
          !fullWidth && "w-max",

          // Link and SVG inheritance
          "[&_a]:!text-inherit [&_svg]:fill-current",

          // Hidden state
          hidden && "invisible",

          // Primary Button Styles - with cyber effects
          primary &&
            !disabled &&
            !active && [
              "bg-primary-600 border-primary-600 text-text-onDark",
              "hover:bg-primary-500 hover:border-primary-500",
              // Neon glow effect
              "shadow-[0_0_10px_rgba(236,25,56,0.3)]",
              "hover:shadow-[0_0_20px_rgba(236,25,56,0.5),0_0_40px_rgba(236,25,56,0.2)]",
              // Cyber sweep effect
              "relative overflow-hidden",
              "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
              "before:translate-x-[-200%] hover:before:translate-x-[200%] before:transition-transform before:duration-700",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
              theme === "dark"
                ? "focus:ring-offset-background-darker"
                : "focus:ring-offset-white",
            ],
          primary &&
            active && [
              "bg-primary-700 border-primary-700 text-text-onDark shadow-inner",
            ],
          primary &&
            disabled && [
              theme === "dark"
                ? "bg-neutral-700 border-neutral-600 text-neutral-500"
                : "bg-neutral-200 border-neutral-200 text-neutral-400",
              "cursor-not-allowed",
            ],

          // Secondary Button Styles
          secondary &&
            !disabled &&
            !active && [
              theme === "dark"
                ? "bg-neutral-700 border-neutral-600 text-text-onDark hover:bg-neutral-600"
                : "bg-neutral-100 border-neutral-200 text-text-onLight hover:bg-neutral-200",
              "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2",
              theme === "dark"
                ? "focus:ring-offset-background-darker"
                : "focus:ring-offset-white",
            ],
          secondary &&
            active && [
              "bg-primary-700 border-primary-700 text-text-onDark shadow-inner",
            ],
          secondary &&
            disabled && [
              theme === "dark"
                ? "bg-neutral-800 border-neutral-700 text-neutral-500"
                : "bg-neutral-50 border-neutral-200 text-neutral-400",
              "cursor-not-allowed",
            ],

          // Default Button Styles (when neither primary, secondary nor borderless)
          !primary &&
            !secondary &&
            !borderLess &&
            !disabled &&
            !active && [
              theme === "dark"
                ? "bg-neutral-700 border-neutral-600 text-text-onDark hover:bg-neutral-600"
                : "bg-white border-neutral-200 text-text-onLight hover:bg-neutral-50",
              "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2",
              theme === "dark"
                ? "focus:ring-offset-background-darker"
                : "focus:ring-offset-white",
            ],
          !primary &&
            !secondary &&
            !borderLess &&
            active && [
              theme === "dark"
                ? "bg-neutral-600 border-neutral-500 text-text-onDark"
                : "bg-neutral-100 border-neutral-400 text-text-onLight",
              "shadow-inner",
            ],
          !primary &&
            !secondary &&
            !borderLess &&
            disabled && [
              theme === "dark"
                ? "bg-neutral-800 border-neutral-700 text-neutral-500"
                : "bg-neutral-50 border-neutral-200 text-neutral-400",
              "cursor-not-allowed",
            ],

          // Borderless Button Styles
          borderLess && "border-none bg-transparent",
          borderLess &&
            !disabled &&
            !active && [
              "text-primary-600 hover:text-primary-500",
              theme === "dark" ? "hover:bg-white/5" : "hover:bg-white/5",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
              theme === "dark"
                ? "focus:ring-offset-background-darker"
                : "focus:ring-offset-white",
            ],
          borderLess &&
            active && [
              "text-primary-700",
              theme === "dark" ? "bg-white/10" : "bg-primary-100",
            ],
          borderLess &&
            disabled && [
              theme === "dark" ? "text-primary-800" : "text-primary-300",
              "cursor-not-allowed",
            ],

          // Custom className last to allow overrides
          customClassName
        )}
      >
        {/* Loading spinner */}
        {loading && (
          <svg
            className="tw-animate-spin tw-w-4 tw-h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="tw-opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="tw-opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {/* Button content */}
        {children}

        {/* Dropdown arrow */}
        {isDropDown && (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

interface DesignSystemButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  variant: ButtonVariant;
  color: ButtonColor;
  state: ButtonState;
  icon?: React.ReactNode;
  isDropDown?: boolean;
  customClassName?: string;
  fullWidth?: boolean;
  width?: number;
  height?: number;
}

const DesignSystemButton = forwardRef<
  HTMLButtonElement,
  DesignSystemButtonProps
>(
  (
    {
      variant,
      color,
      state,
      icon,
      isDropDown,
      disabled,
      children,
      type = "button",
      customClassName,
      fullWidth,
      width,
      height,
      ...rest
    },
    ref
  ) => {
    // Text color per Figma (Default | Primary | Secondary × Default | Hovered | Selected)
    const textColor = (() => {
      if (disabled) return "text-neutral-500";
      if (variant === "fill") return "text-white";
      if (state === "selected") {
        if (color === "secondary") return "text-secondary-600";
        return "text-primary-700";
      }
      if (state === "hovered") {
        if (color === "primary") return "text-primary-400";
        if (color === "secondary") return "text-secondary-400";
        return "text-primary-600";
      }
      // default state
      if (color === "primary") return "text-primary-600";
      if (color === "secondary") return "text-secondary-500";
      if (variant === "text-bold" || variant === "link")
        return "text-text-highlight";
      return "text-text-default";
    })();

    if (variant === "fill") {
      const isPrimary = color === "primary";

      // Inner pill background per state. Primary keeps a red border at all times;
      // default-state primary stays dark with the red border showing through.
      const innerBg = isPrimary
        ? state === "default"
          ? "bg-surface-sunken hover:bg-primary-600"
          : "bg-primary-600"
        : state === "selected"
        ? "bg-neutral-700"
        : state === "hovered"
        ? "bg-neutral-500"
        : "bg-neutral-600 hover:bg-neutral-500";

      // Inner pill border: red for primary, transparent (none) for default.
      const innerBorder = isPrimary
        ? "border-2 border-solid border-primary-600"
        : "border-0";

      // Outer ring: a full grey rounded border, with a fixed ~57px red cap on the
      // RIGHT (Figma "Rectangle 40 Copy" = 56.757px, constant regardless of button
      // width). Selected turns the whole ring red. Non-primary stays plain grey.
      const ringBaseColor =
        isPrimary && state === "selected" ? "#ff002d" : "var(--color-border-subtle)";

      return (
        <button
          ref={ref}
          type={type}
          disabled={disabled}
          style={{
            width: width ? `${width}px` : undefined,
            height: height ? `${height}px` : undefined,
          }}
          className={clsx(
            "group/btn relative inline-flex items-center justify-center rounded-full",
            "px-6 py-3 cursor-pointer transition-colors",
            "[font-family:var(--font-avenir)] font-semibold text-[14px] tracking-[1.25px] uppercase text-white",
            innerBg,
            innerBorder,
            disabled && "cursor-not-allowed opacity-60",
            fullWidth && "w-full",
            customClassName
          )}
          {...rest}
        >
          {/* Outer ring — wraps the pill with a 4px gap (Figma p-[4px]). */}
          {/* base full ring (grey, or red when selected) */}
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-[4px] rounded-full border-[1.5px] border-solid"
            style={{ borderColor: ringBaseColor }}
          />
          {/* fixed ~57px red cap on the right (primary only) */}
          {isPrimary && (
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-[4px] rounded-full border-[1.5px] border-solid"
              style={{ borderColor: "#ff002d", clipPath: "inset(0 0 0 calc(100% - 57px))" }}
            />
          )}
          <span className="relative">{children}</span>
        </button>
      );
    }

    const baseTextClass = clsx(
      "inline-flex items-center justify-center gap-1 rounded-[100px] cursor-pointer",
      "[font-family:var(--font-avenir)]",
      variant === "text-bold"
        ? "font-semibold text-[16px] leading-6 tracking-[0.5px]"
        : variant === "text-icon"
        ? "font-semibold text-[14px] leading-none tracking-[1.25px] uppercase"
        : "font-normal text-[16px] leading-7 tracking-[0.5px]",
      variant === "link" && "underline decoration-solid",
      textColor,
      disabled && "cursor-not-allowed opacity-60"
    );

    // text-icon variant defaults to a chevron when no icon is supplied.
    const showChevron = isDropDown || variant === "text-icon";
    const trailing = icon ?? (showChevron ? (
      <span className="inline-flex size-6 items-center justify-center">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={clsx(
            "transition-transform",
            state === "selected" && "rotate-180"
          )}
        >
          <path
            d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    ) : null);

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        style={{
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
        }}
        className={clsx(baseTextClass, fullWidth && "w-full", customClassName)}
        {...rest}
      >
        {children}
        {trailing}
      </button>
    );
  }
);

DesignSystemButton.displayName = "DesignSystemButton";

export default Button;

// For case duplicate name when import
export { Button };
